import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, PieChart, Pie, Cell, LineChart, Line, ResponsiveContainer } from "recharts";
import { BarChart3, TrendingUp, Users, Baby, CalendarDays, Loader2, FileText } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, isWithinInterval } from "date-fns";
import { ar } from "date-fns/locale";
import MonthlyChildReport from "@/components/reports/MonthlyChildReport";
import MonthlyPaymentReport from "@/components/reports/MonthlyPaymentReport";

const COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--destructive))",
  "hsl(var(--accent))",
  "hsl(var(--secondary))",
];

const chartConfig = {
  present: { label: "حاضر", color: "hsl(var(--primary))" },
  absent: { label: "غائب", color: "hsl(var(--destructive))" },
  late: { label: "متأخر", color: "hsl(var(--accent))" },
  children: { label: "أطفال", color: "hsl(var(--primary))" },
  staff: { label: "موظفين", color: "hsl(var(--secondary))" },
};

const Reports = () => {
  const { profile } = useAuth();
  const nurseryId = profile?.nursery_id;
  const [period, setPeriod] = useState<"week" | "month">("week");
  const [attendeeType, setAttendeeType] = useState<"child" | "staff">("child");

  const now = new Date();
  const dateRange = useMemo(() => {
    if (period === "week") {
      return { start: startOfWeek(now, { weekStartsOn: 0 }), end: endOfWeek(now, { weekStartsOn: 0 }) };
    }
    return { start: startOfMonth(now), end: endOfMonth(now) };
  }, [period]);

  const { data: attendanceData, isLoading } = useQuery({
    queryKey: ["attendance-report", nurseryId, period, attendeeType],
    queryFn: async () => {
      if (!nurseryId) return [];
      const { data, error } = await supabase
        .from("attendance")
        .select("*")
        .eq("nursery_id", nurseryId)
        .eq("attendee_type", attendeeType)
        .gte("date", format(dateRange.start, "yyyy-MM-dd"))
        .lte("date", format(dateRange.end, "yyyy-MM-dd"));
      if (error) throw error;
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const { data: childrenCount } = useQuery({
    queryKey: ["children-count", nurseryId],
    queryFn: async () => {
      if (!nurseryId) return 0;
      const { count } = await supabase.from("children").select("*", { count: "exact", head: true }).eq("nursery_id", nurseryId);
      return count || 0;
    },
    enabled: !!nurseryId,
  });

  const { data: staffCount } = useQuery({
    queryKey: ["staff-count", nurseryId],
    queryFn: async () => {
      if (!nurseryId) return 0;
      const { count } = await supabase.from("staff").select("*", { count: "exact", head: true }).eq("nursery_id", nurseryId);
      return count || 0;
    },
    enabled: !!nurseryId,
  });

  const totalMembers = attendeeType === "child" ? (childrenCount || 0) : (staffCount || 0);

  // Daily breakdown for bar chart
  const dailyData = useMemo(() => {
    const days = eachDayOfInterval({ start: dateRange.start, end: now > dateRange.end ? dateRange.end : now });
    return days.map((day) => {
      const dayStr = format(day, "yyyy-MM-dd");
      const dayRecords = (attendanceData || []).filter((r) => r.date === dayStr);
      return {
        date: format(day, "EEE d", { locale: ar }),
        present: dayRecords.filter((r) => r.status === "present").length,
        absent: dayRecords.filter((r) => r.status === "absent").length,
        late: dayRecords.filter((r) => r.status === "late").length,
      };
    });
  }, [attendanceData, dateRange]);

  // Pie chart summary
  const pieData = useMemo(() => {
    const records = attendanceData || [];
    const present = records.filter((r) => r.status === "present").length;
    const absent = records.filter((r) => r.status === "absent").length;
    const late = records.filter((r) => r.status === "late").length;
    const total = present + absent + late;
    if (total === 0) return [];
    return [
      { name: "حاضر", value: present, percentage: Math.round((present / total) * 100) },
      { name: "غائب", value: absent, percentage: Math.round((absent / total) * 100) },
      { name: "متأخر", value: late, percentage: Math.round((late / total) * 100) },
    ];
  }, [attendanceData]);

  // Attendance rate line chart
  const rateData = useMemo(() => {
    if (!totalMembers) return [];
    const days = eachDayOfInterval({ start: dateRange.start, end: now > dateRange.end ? dateRange.end : now });
    return days.map((day) => {
      const dayStr = format(day, "yyyy-MM-dd");
      const dayRecords = (attendanceData || []).filter((r) => r.date === dayStr);
      const presentCount = dayRecords.filter((r) => r.status === "present" || r.status === "late").length;
      const rate = totalMembers > 0 ? Math.round((presentCount / totalMembers) * 100) : 0;
      return { date: format(day, "EEE d", { locale: ar }), rate };
    });
  }, [attendanceData, dateRange, totalMembers]);

  const avgRate = useMemo(() => {
    if (!rateData.length) return 0;
    return Math.round(rateData.reduce((sum, d) => sum + d.rate, 0) / rateData.length);
  }, [rateData]);

  const totalPresent = pieData.find((d) => d.name === "حاضر")?.value || 0;
  const totalAbsent = pieData.find((d) => d.name === "غائب")?.value || 0;
  const totalLate = pieData.find((d) => d.name === "متأخر")?.value || 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">التقارير والإحصائيات</h1>
        <p className="text-muted-foreground text-sm mt-1">تقارير الحضور التفاعلية والتقارير الشهرية</p>
      </div>

      <Tabs defaultValue="attendance" dir="rtl">
        <TabsList>
          <TabsTrigger value="attendance" className="gap-1.5">
            <BarChart3 className="w-4 h-4" /> إحصائيات الحضور
          </TabsTrigger>
          <TabsTrigger value="monthly" className="gap-1.5">
            <FileText className="w-4 h-4" /> التقرير الشهري
          </TabsTrigger>
          <TabsTrigger value="financial" className="gap-1.5">
            <TrendingUp className="w-4 h-4" /> التقرير المالي
          </TabsTrigger>
        </TabsList>

        <TabsContent value="attendance" className="mt-4 space-y-6">
          <div className="flex justify-end gap-2">
            <Select value={attendeeType} onValueChange={(v) => setAttendeeType(v as "child" | "staff")}>
              <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="child">الأطفال</SelectItem>
                <SelectItem value="staff">الموظفين</SelectItem>
              </SelectContent>
            </Select>
            <Select value={period} onValueChange={(v) => setPeriod(v as "week" | "month")}>
              <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="week">هذا الأسبوع</SelectItem>
                <SelectItem value="month">هذا الشهر</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "نسبة الحضور", value: `${avgRate}%`, icon: TrendingUp, color: "text-primary" },
              { label: "إجمالي الحضور", value: totalPresent, icon: Users, color: "text-primary" },
              { label: "إجمالي الغياب", value: totalAbsent, icon: CalendarDays, color: "text-destructive" },
              { label: "إجمالي التأخير", value: totalLate, icon: BarChart3, color: "text-muted-foreground" },
            ].map((s, i) => (
              <Card key={i} className="shadow-card">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                    <s.icon className={`w-5 h-5 ${s.color}`} />
                  </div>
                  <div>
                    <div className="text-xl font-bold text-foreground">{isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : s.value}</div>
                    <div className="text-xs text-muted-foreground">{s.label}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Tabs defaultValue="bar" className="space-y-4">
            <TabsList>
              <TabsTrigger value="bar">الحضور اليومي</TabsTrigger>
              <TabsTrigger value="line">معدل الحضور</TabsTrigger>
              <TabsTrigger value="pie">التوزيع العام</TabsTrigger>
            </TabsList>

            <TabsContent value="bar">
              <Card className="shadow-card">
                <CardHeader><CardTitle className="text-lg">الحضور والغياب اليومي</CardTitle></CardHeader>
                <CardContent>
                  {isLoading ? (
                    <div className="h-[300px] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
                  ) : dailyData.length === 0 ? (
                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">لا توجد بيانات</div>
                  ) : (
                    <ChartContainer config={chartConfig} className="h-[300px] w-full">
                      <BarChart data={dailyData}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                        <XAxis dataKey="date" className="text-xs" />
                        <YAxis allowDecimals={false} />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Bar dataKey="present" fill="var(--color-present)" radius={[4, 4, 0, 0]} name="حاضر" />
                        <Bar dataKey="absent" fill="var(--color-absent)" radius={[4, 4, 0, 0]} name="غائب" />
                        <Bar dataKey="late" fill="var(--color-late)" radius={[4, 4, 0, 0]} name="متأخر" />
                      </BarChart>
                    </ChartContainer>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="line">
              <Card className="shadow-card">
                <CardHeader><CardTitle className="text-lg">معدل الحضور اليومي (%)</CardTitle></CardHeader>
                <CardContent>
                  {isLoading ? (
                    <div className="h-[300px] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
                  ) : rateData.length === 0 ? (
                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">لا توجد بيانات</div>
                  ) : (
                    <ChartContainer config={chartConfig} className="h-[300px] w-full">
                      <LineChart data={rateData}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                        <XAxis dataKey="date" className="text-xs" />
                        <YAxis domain={[0, 100]} unit="%" />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Line type="monotone" dataKey="rate" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ fill: "hsl(var(--primary))" }} name="نسبة الحضور" />
                      </LineChart>
                    </ChartContainer>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="pie">
              <Card className="shadow-card">
                <CardHeader><CardTitle className="text-lg">التوزيع العام للحضور</CardTitle></CardHeader>
                <CardContent>
                  {isLoading ? (
                    <div className="h-[300px] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
                  ) : pieData.length === 0 ? (
                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">لا توجد بيانات</div>
                  ) : (
                    <div className="flex flex-col md:flex-row items-center gap-8">
                      <ChartContainer config={chartConfig} className="h-[280px] w-[280px]">
                        <PieChart>
                          <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} dataKey="value" nameKey="name" label={({ percentage }) => `${percentage}%`}>
                            {pieData.map((_, i) => (
                              <Cell key={i} fill={COLORS[i % COLORS.length]} />
                            ))}
                          </Pie>
                          <ChartTooltip content={<ChartTooltipContent />} />
                        </PieChart>
                      </ChartContainer>
                      <div className="flex flex-col gap-3">
                        {pieData.map((d, i) => (
                          <div key={i} className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i] }} />
                            <span className="text-sm text-foreground">{d.name}: <strong>{d.value}</strong> ({d.percentage}%)</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </TabsContent>

        <TabsContent value="monthly" className="mt-4">
          <MonthlyChildReport />
        </TabsContent>

        <TabsContent value="financial" className="mt-4">
          <MonthlyPaymentReport />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Reports;
